import { useState } from "react";
import { toast } from "sonner";
import { X, Download } from "lucide-react";
import { postWebsiteLead } from "@/lib/leadCapture";
import { sanitizeNameInput } from "@/lib/formInput";
import PhoneInput, { isValidPhone } from "@/components/ui-shared/PhoneInput";

const CATALOGUE_URL = "/catalogue/SpringXE-Course-Catalogue-2026.pdf";

const CatalogueModal = ({ open, onClose }) => {
    const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", role: "" });
    const [loading, setLoading] = useState(false);

    if (!open) return null;

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.role.trim()) {
            toast.error("Please fill in all the fields.");
            return;
        }
        if (!isValidPhone(form.phone)) {
            toast.error("Please enter a valid 10-digit phone number.");
            return;
        }
        setLoading(true);
        postWebsiteLead(form, "catalogue-request");

        // A real catalogue PDF is bundled with the site — download it directly,
        // no backend required. Lead capture already went to the ERP above.
        const a = document.createElement("a");
        a.href = CATALOGUE_URL;
        a.download = "SpringXE-Course-Catalogue-2026.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Your catalogue is downloading. We will be in touch soon.");
        setForm({ name: "", email: "", company: "", phone: "", role: "" });
        setLoading(false);
        onClose();
    };

    return (
        <div
            data-testid="catalogue-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-up"
            onClick={onClose}
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={onSubmit}
                className="w-full max-w-lg sxe-form-card rounded-2xl p-6 md:p-8 relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    data-testid="catalogue-modal-close"
                    className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[#150826]/10 text-[#150826] hover:border-[#A100FF]/50 hover:bg-[#A100FF]/5 flex items-center justify-center"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center">
                    <Download className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display text-[#150826] text-2xl md:text-3xl font-medium tracking-tight mt-5">
                    Download the SpringXE Catalogue
                </h3>
                <p className="text-[#6B5980] text-sm mt-2">
                    Share a few details and we&apos;ll open the enterprise catalogue instantly, plus keep you posted on new programs.
                </p>

                <div className="grid md:grid-cols-2 gap-3 mt-6">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Full Name*</label>
                        <input data-testid="catalogue-input-name" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: sanitizeNameInput(e.target.value) })} className="sxe-form-input mt-1" placeholder="Your name" required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Work Email*</label>
                        <input data-testid="catalogue-input-email" name="email" type="email" value={form.email} onChange={onChange} className="sxe-form-input mt-1" placeholder="you@company.com" required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Phone Number*</label>
                        <PhoneInput testId="catalogue-input-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Company*</label>
                        <input data-testid="catalogue-input-company" name="company" value={form.company} onChange={onChange} className="sxe-form-input mt-1" placeholder="Company name" required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Role*</label>
                        <input data-testid="catalogue-input-role" name="role" value={form.role} onChange={onChange} className="sxe-form-input mt-1" placeholder="e.g. Head of L&amp;D" required />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    data-testid="catalogue-submit-btn"
                    className="btn-primary w-full mt-6 disabled:opacity-60"
                >
                    {loading ? "Preparing your catalogue..." : (<>Download Catalogue <Download className="w-4 h-4" /></>)}
                </button>
            </form>
        </div>
    );
};

export default CatalogueModal;
